import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Space, Spin, Statistic } from 'antd';
import type { LocationFilterType } from '../../../hooks/SimViewer/useDataFilter';
import { useMetaData } from '../../../hooks/data/useMetaData';
import { useAreasData } from '../../../hooks/data/useAreasData';
import { useAreaAggregationComparisonsData } from '../../../hooks/data/useAreaAggregationComparisonsData';
import { useSummaryComparisonsData } from '../../../hooks/data/useSummaryComparisonsData';
import { BarChart, type BarItem } from '../Chart/BarChart';
import { SchemaSelector } from './SchemaSelector';

type Props = {
  type: Extract<LocationFilterType, 'start' | 'end' | 'home' | 'work'>;
  noDataMessage: string;
  unit?: string;
};

const AreaAggregationComparison = ({
  type,
  noDataMessage,
  unit,
}: Props): ReactNode => {
  const { t } = useTranslation();
  const { allDataMetas } = useMetaData();
  const { findArea } = useAreasData();
  const { isLoading: isLoadingAggregation, aggregationsMap } =
    useAreaAggregationComparisonsData(type);
  const { isLoading: isLoadingSummary, summariesMap } =
    useSummaryComparisonsData();

  return (
    <>
      <SchemaSelector />
      <Spin spinning={isLoadingAggregation || isLoadingSummary}>
        <GridContainer>
          {Object.entries(aggregationsMap).map(
            ([simDataId, aggregations]: [
              string,
              Map<string, number>,
            ]): ReactNode => {
              // 集計値の大きい順に上位10件を表示する
              const ranking = Array.from(aggregations)
                .sort(
                  (
                    [, value1]: [string, number],
                    [, value2]: [string, number]
                  ): number => {
                    return -(value1 - value2);
                  }
                )
                .slice(0, 10)
                .map(([keyCode, value]: [string, number]): BarItem => {
                  return {
                    key: keyCode,
                    value: value,
                    label: findArea(keyCode)?.s_name ?? `(${t('unknown')})`,
                  };
                });
              return (
                <Space
                  key={simDataId}
                  direction="vertical"
                  style={{ width: '100%' }}
                >
                  <Statistic
                    title={<ItemTitle>{t('targetAgentCount')}</ItemTitle>}
                    value={summariesMap[simDataId] ?? '-'}
                    suffix={<ItemSuffix>{t('unit.agent')}</ItemSuffix>}
                  />
                  <BarChart
                    data={ranking}
                    noDataMessage={noDataMessage}
                    unit={unit}
                  />
                  <GraphTitle>
                    {allDataMetas.get(simDataId)?.name ?? ''}
                  </GraphTitle>
                </Space>
              );
            }
          )}
        </GridContainer>
      </Spin>
    </>
  );
};

const GridContainer = styled.div`
  margin: 16px 0px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px 24px;
`;

const ItemTitle = styled.div`
  color: #595959;
`;

const ItemSuffix = styled.div`
  font-size: 60%;
`;

const GraphTitle = styled.div`
  font-size: 12px;
  text-align: center;
`;

export { AreaAggregationComparison };
